import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { BodyText, Switch, TextField, theme } from '@sg/garnish';
import { CardType } from '@sg/graphql-schema';

import { CreditCardFormBlockedFields } from './CreditCardFormBlockedFields';
import { CreditCardFormFooter } from './CreditCardFormFooter';
import { CreditCardFormLineItem } from './CreditCardFormLineItem';

/**
 * Section of the credit card form controlled by sweetgreen.
 * As opposed to the sections controlled by Stripe.
 */
export const CreditCardFormControlledContent = (props: ControlledFormProps) => {
  const {
    children,
    description,
    cardType = PAYMENT_ADD,
    isSaving,
    isDeleting,
    mode,
    canSubmitForm,
    form,
    excludedFields = [],
    onFormChange,
    onSubmitForm,
    onDelete,
  } = props;

  const { formatMessage } = useIntl();

  // ─── Callbacks ───────────────────────────────────────────────────────

  const updateFormField = useCallback(
    (field: CreditCardField) => (value: string | number | boolean) => {
      onFormChange({ ...form, [field]: value });
    },
    [form, onFormChange],
  );

  return (
    <View testID="credit-card-form" style={styles.container}>
      <View style={styles.content}>
        <View style={styles.frameContainer}>{children}</View>

        {/* Used when editing credit card, fields disabled */}
        <CreditCardFormBlockedFields
          show={mode === 'edit'}
          description={description}
          cardType={getCreditCardIcon(cardType)}
        />

        {/* Nickname field, not controlled by Braintree */}
        {!excludedFields.includes('nickname') && (
          <CreditCardFormLineItem>
            <TextField
              testID="credit-card-form.nickname"
              label={formatMessage(messages.nickname)}
              value={form.nickname}
              onChangeText={updateFormField('nickname')}
            />
          </CreditCardFormLineItem>
        )}

        {/* Default credit card switch, not controlled by Stripe */}
        {!excludedFields.includes('isDefault') && (
          <CreditCardFormLineItem>
            <View style={styles.row}>
              <View style={styles.column}>
                <BodyText size={3}>{formatMessage(messages.default)}</BodyText>
              </View>

              <Switch
                testID="credit-card-form.default"
                value={form.default}
                accessibilityLabel={formatMessage(messages.default)}
                onChange={updateFormField('default')}
              />
            </View>
          </CreditCardFormLineItem>
        )}
      </View>

      {/* Save / Remove footer, not controlled by Stripe */}
      <CreditCardFormFooter
        mode={mode}
        isSaving={isSaving}
        isDeleting={isDeleting}
        canSubmit={canSubmitForm}
        onDelete={onDelete}
        onSubmit={onSubmitForm}
      />
    </View>
  );
};

// ─── Constants ─────────────────────────────────────────────────────────

export const APPLE_PAY = 'ApplePay';
export const PAYMENT_ADD = 'ADD';

// ─── Messages ──────────────────────────────────────────────────────────

const messages = defineMessages({
  default: {
    defaultMessage: 'Set as default payment method',
    description: 'Credit Card Form | Default',
  },
  nickname: {
    defaultMessage: 'Card nickname',
    description: 'Credit Card Form | Nickname',
  },
});

// ─── Types ─────────────────────────────────────────────────────────────

type CreditCardField = keyof CreditCardForm;
type CreditCardForm = Readonly<{
  nickname: string;
  default: boolean;
}>;

type ControlledFormProps = Readonly<{
  children?: ViewProps['children'];
  description?: string;
  cardType?: CardType;
  isSaving?: boolean;
  isDeleting?: boolean;
  canSubmitForm: boolean;
  form: CreditCardForm;
  mode: 'add' | 'edit' | 'replace';
  excludedFields?: ReadonlyArray<'nickname' | 'isDefault'>;
  onFormChange: (form: CreditCardForm) => void;
  onSubmitForm: () => void;
  onDelete?: () => void;
}>;

// ─── Styles ────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'space-between',
    gap: theme.spacing['6'],
  },
  content: {
    flex: 1,
    gap: theme.spacing['6'],
  },
  frameContainer: {
    position: 'relative',
  },
  column: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
  },
});

// ─── Helpers ───────────────────────────────────────────────────────────

function getCreditCardIcon(
  cardType:
    | CardType
    | typeof PAYMENT_ADD
    | typeof APPLE_PAY = CardType.Unsupported,
  shouldUseAddIcon?: boolean,
) {
  switch (cardType) {
    case CardType.Amex:
    case CardType.AmericanExpress: {
      return 'IconCcAmex';
    }

    case CardType.Discover: {
      return 'IconCcDiscover';
    }

    case CardType.MasterCard: {
      return 'IconCcMasterCard';
    }

    case CardType.Visa: {
      return 'IconCcVisa';
    }

    case APPLE_PAY: {
      return 'IconApplePayPayment';
    }

    case PAYMENT_ADD: {
      return shouldUseAddIcon ? 'IconPlus' : 'IconCreditCard';
    }

    default: {
      return 'IconCreditCard';
    }
  }
}
